import { JetView } from 'webix-jet'; // Importing JetView from the webix-jet library
import {
    profile, // User profile information
    devConfig, // Development configuration settings
    countries, // List of countries
    paymentSystem, // Payment system configurations
    cashDesks, // Cash desk configurations
    agentList, // List of agents
} from 'config/config'; // Importing configuration data from the config module

/**
 * Class representing the FilterInformeGerencialView.
 * @extends JetView
 */
export default class FilterInformeGerencialView extends JetView {
    /**
     * Defines the configuration for the view.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        // Retrieve the localization service from the app
        const _ = this.app.getService('locale')._;
        // Define the left section of the UI with margin and rows
        const left = {
            margin: 5,
            rows: [
                {
                    margin: 15,
                    css: 'on-row',
                    cols: [
                        {
                            // Datepicker input field
                            view: 'text',
                            name: 'Datepicker',
                            id: 'Datepicker',
                            css: 'reportrange',
                            readonly: true,
                            label: "<span class='webix_icon icon-calendar'></span>" + _('Fecha'), // Localized label for date
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Localized placeholder
                        },
                        {
                            // Country selection dropdown
                            view: 'richselect',
                            name: 'CountrySelect',
                            id: 'CountrySelect',
                            label: "<span class='icon-language'></span>" + _('País'), // Localized label for country
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Localized placeholder
                            options: {
                                body: {
                                    // Template for displaying country options with flags
                                    template: function (obj) {
                                        return "<i class='flags " + obj.icon + "'></i>" + obj.value;
                                    },
                                    data: countries, // Data source for country options
                                },
                            },
                            // Conditional visibility based on configuration
                            hidden: devConfig.getCountryReport() || !devConfig.getPermAction('filterByCountry', 'Add'),
                            css: 'prm-filterByCountry',
                        },
                        {
                            // Second country selection dropdown (readonly)
                            view: 'richselect',
                            name: 'CountrySelect2',
                            label: "<span class='icon-language'></span>" + _('País'), // Localized label for country
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Localized placeholder
                            value: profile.Settings.ReportCountry, // Default value from profile settings
                            readonly: true,
                            options: {
                                body: {
                                    // Template for displaying country options with flags
                                    template: function (obj) {
                                        return "<i class='flags " + obj.icon + "'></i>" + obj.value;
                                    },
                                    data: countries, // Data source for country options
                                },
                            },
                            // Conditional visibility based on configuration
                            hidden: !devConfig.getCountryReport() || !devConfig.getPermAction('filterByCountry', 'Add'),
                            css: 'prm-filterByCountry',
                            css: 'country-hide',
                        },
                        {
                            // Type of bet selection dropdown
                            view: 'richselect',
                            name: 'TypeBet',
                            label: _('Tipo de Apuesta'), // Localized label for bet type
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Localized placeholder
                            value: 2, // Default value
                            options: [
                                { id: '1', value: _('Apuestas Creadas') }, // Localized option
                                { id: '2', value: _('Apuestas Cerradas') }, // Localized option
                                { id: '3', value: _('Apuestas Pagadas') }, // Localized option
                            ],
                        },
                    ],
                },
                {
                    margin: 15,
                    css: 'on-row',
                    cols: [
                        {
                            // Hidden column for user filter permission
                            hidden: devConfig.getPermAction('filterByUser', 'Add'),
                            css: 'prm-filterByUser',
                            height: 1,
                        },
                        {
                            // User ID input field
                            view: 'text',
                            name: 'UserId',
                            type: 'number',
                            label: "<span class='icon-drivers-license-o'></span>" + _('ID Usuario'), // Localized label for user ID
                            hidden: !devConfig.getPermAction('filterByUser', 'Add'),
                            css: 'prm-filterByUser',
                            labelPosition: 'top',
                            placeholder: _('ID Usuario'), // Localized placeholder
                        },
                        {
                            // User name input field
                            view: 'text',
                            name: 'Name',
                            label: "<span class='icon-user'></span>" + _('Nombre'), // Localized label for name
                            labelPosition: 'top',
                            placeholder: _('Nombre'), // Localized placeholder
                            hidden: !devConfig.getPermAction('filterByUser', 'Add'),
                            css: 'prm-filterByUser',
                        },
                        {
                            // User login input field
                            view: 'text',
                            name: 'Login',
                            label: _('Login'), // Localized label for login
                            labelPosition: 'top',
                            placeholder: _('Login'), // Localized placeholder
                            hidden: !devConfig.getPermAction('filterByUser', 'Add'),
                            css: 'prm-filterByUser',
                        },
                    ],
                },
            ],
        };
        // Define the upper section of the UI with responsive height
        const upper_section = {
            css: 'father-container',
            height: document.body.offsetWidth < 500 ? 180 : 0,
            rows: [
                {
                    margin: 10,
                    cols: [left], // Include the left section
                },
            ],
        };
        // Define the buttons section with actions
        const buttons = {
            id: 'b2',
            rows: [
                {
                    margin: 10,
                    responsive: 'b2',
                    cols: [
                        { id: 'grav', gravity: 4 }, // Gravity for layout
                        {
                            // Clear button
                            view: 'button',
                            value: _('Limpiar'), // Localized label for clear
                            width: screen == 'small' ? 0 : 100,
                            minWidth: 200,
                            click: () => {
                                this.getRoot().clear(); // Clear the form
                            },
                        },
                        {
                            // Consult button
                            view: 'button',
                            value: _('Consultar'), // Localized label for consult
                            hotkey: 'enter', // Shortcut key
                            type: 'form',
                            width: screen == 'small' ? 0 : 100,
                            minWidth: 200,
                            click: () => {
                                const filterdata = this.getRoot().getValues(); // Get form values
                                if (devConfig.getCountryReport()) {
                                    filterdata.CountrySelect = profile.Settings.ReportCountry; // Set country if report is enabled
                                }
                                delete filterdata['CountrySelect2']; // Remove unused field
                                this.app.callEvent('informeGerencial:filter', [
                                    filterdata, // Trigger filter event with data
                                ]);
                            },
                        },
                    ],
                },
            ],
        };
        // Define a second buttons section for a different form
        const buttons2 = {
            padding: 10,
            cols: [
                {
                    // Clear button for second form
                    view: 'button',
                    value: _('Limpiar'), // Localized label for clear
                    click: () => {
                        $$('myForm2').clear(); // Clear the second form
                    },
                },
                {
                    // Consult button for second form
                    view: 'button',
                    value: _('Consultar'), // Localized label for consult
                    type: 'form',
                    click: () => {
                        const filterdata = $$('myForm2').getValues(); // Get values from second form
                        if (profile.typeDate !== 2) {
                            delete filterdata['Date']; // Remove date if not applicable
                        } else {
                            if (true) {
                                delete filterdata['Datepicker']; // Remove datepicker if applicable
                            }
                        }
                        this.app.callEvent('informeGerencial:filter', [
                            filterdata, // Trigger filter event with data
                        ]);
                        $$('filterPopup').hide(); // Hide the filter popup
                    },
                },
            ],
        };
        // Define the second accordion section
        const accordion_section2 = {
            type: 'wide',
            rows: [
                {
                    multi: true,
                    view: 'accordion',
                    css: 'betShop',
                    collapsed: true,
                    type: 'clean',
                    rows: [
                        {
                            header: _('Filtros'), // Localized header for filters
                            id: 'b5',
                            body: {
                                padding: 10,
                                rows: [upper_section, buttons], // Include upper section and buttons
                            },
                        },
                    ],
                },
            ],
        };
        // Define the third accordion section with scroll view
        const accordion_section3 = {
            view: 'scrollview',
            scroll: 'xy',
            body: {
                padding: 10,
                rows: [upper_section], // Include upper section
            },
        };
        // Define the filter popup window
        const filterPopup = webix.ui({
            view: 'window',
            move: true,
            id: 'filterPopup',
            fullscreen: true,
            head: {
                view: 'toolbar',
                cols: [
                    { view: 'label', label: _('Filtros'), align: 'center' }, // Localized label for filters
                    {
                        // Close icon for the popup
                        view: 'icon',
                        icon: 'wxi-close',
                        click: function () {
                            this.getTopParentView().hide(); // Hide the popup on click
                        },
                    },
                ],
            },
            modal: true,
            position: 'center',
            body: {
                view: 'form',
                id: 'myForm2',
                type: 'clean',
                rows: [accordion_section3, buttons2], // Include accordion section and buttons
                rules: {
                    fname: webix.rules.isNotEmpty, // Validation rule for form
                },
            },
        });
        // Return the main form structure
        return {
            view: 'form',
            type: 'clean',
            rows: [
                screen.width > 500 && screen.height > 500
                    ? accordion_section2 // Include accordion section if screen size is sufficient
                    : { height: 1, width: 0 }, // Placeholder for small screens
            ],
            rules: {
                fname: webix.rules.isNotEmpty, // Validation rule for form
            },
        }; // Placeholder for the configuration content
    }

    /**
     * Initializes the view with the provided grid.
     * @param {Object} grid - The grid instance to initialize.
     */
    init(grid) {
        // Retrieve the current language setting from the locale service
        const lang = this.app.getService('locale').getLang();

        // Execute the following code when the Webix library is ready
        webix.ready(function () {
            // Attach an event handler for the 'onAfterRender' event of the Datepicker component
            $$('Datepicker').attachEvent('onAfterRender', function () {
                // Set the date modal configuration based on the current language
                devConfig.setDateModal(lang);
            });
        });

        // Initiate an AJAX request using the Webix library
        webix
            .ajax()
            .headers({
                // Set the content type of the request to JSON
                'Content-type': 'application/json',
            })
            .post(devConfig.geturlApi() + 'setting/GetSetting', '', {
                // Define the error callback function for the AJAX request
                error: function (text, data, XmlHttpRequest) {
                    // Display an error message if the request fails
                    webix.message(_('Error! '));
                },
                // Define the success callback function for the AJAX request
                success: function (text, data, XmlHttpRequest) {
                    // Parse the response text into a JSON object
                    var json = JSON.parse(text);
                    // Check if the response indicates an error
                    if (json.HasError) {
                        // Display an error message if there is an error in the response
                        webix.message(_('Error! '));
                    } else {
                        // Parse and transform the response data for use in the application
                        var data = JSON.parse(
                            text
                                .replace(/"Id"/g, '"id"') // Normalize 'Id' to 'id'
                                .replace(/"Name"/g, '"value"') // Normalize 'Name' to 'value'
                                .replace(/"DisplayName"/g, '"value"'), // Normalize 'DisplayName' to 'value'
                        );
                        // Define the options for the CurrencySelect component with the retrieved currencies
                        $$('CurrencySelect').define('options', data.Data.ReportCurrencies);
                        // Refresh the CurrencySelect component to reflect the new options
                        $$('CurrencySelect').refresh();
                    }
                },
            }); // Placeholder for the initialization content
    }
}

// FILE DOCUMENTED
